import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalFooter,
  Button,
  Icon,
  MultiColumnList,
  MessageBanner,
  Layout,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';

const STEP_REVIEW = 'review';
const STEP_RESULT = 'result';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Step 1 data — from sync endpoint
  newKeys: PropTypes.object,        // { translationKey: sourceValue }
  orphanedKeys: PropTypes.arrayOf(PropTypes.shape({
    translationKey: PropTypes.string,
    locales: PropTypes.arrayOf(PropTypes.string),
  })),
  fileName: PropTypes.node,
  // Step 1 actions
  onCreateOnly: PropTypes.func.isRequired,   // create new, ignore orphaned
  onCreateAndDelete: PropTypes.func.isRequired, // create new + delete orphaned
};

const defaultProps = {
  newKeys: {},
  orphanedKeys: [],
};

const CreateNewKeysModal = ({
  open,
  onClose,
  newKeys,
  orphanedKeys,
  fileName,
  onCreateOnly,
  onCreateAndDelete,
}) => {
  const intl = useIntl();
  const [step, setStep] = useState(STEP_REVIEW);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const newKeysArray = Object.entries(newKeys || {}).map(([k, v]) => ({
    id: k, translationKey: k, sourceValue: v,
  }));

  const hasNew      = newKeysArray.length > 0;
  const hasOrphaned = orphanedKeys.length > 0;

  const handleClose = () => {
    setStep(STEP_REVIEW);
    setResult(null);
    onClose();
  };

  const handleCreateOnly = async () => {
    setIsLoading(true);
    try {
      const res = await onCreateOnly();
      setResult({ created: res?.created ?? newKeysArray.length, deleted: 0, error: null });
    } catch (e) {
      setResult({ created: 0, deleted: 0, error: e.message });
    }
    setIsLoading(false);
    setStep(STEP_RESULT);
  };

  const handleCreateAndDelete = async () => {
    setIsLoading(true);
    try {
      const res = await onCreateAndDelete();
      setResult({
        created: res?.created ?? newKeysArray.length,
        deleted: res?.deleted ?? orphanedKeys.length,
        error: null,
      });
    } catch (e) {
      setResult({ created: 0, deleted: 0, error: e.message });
    }
    setIsLoading(false);
    setStep(STEP_RESULT);
  };

  // ── Step 1: Review ────────────────────────────────────────────────
  const renderReview = () => (
    <>
      {hasNew ? (
        <>
          <MessageBanner type="success">
            <FormattedMessage
              id="stripes-smart-components.actionMenu.sync.newKeysCount"
              defaultMessage="{count} new key(s) will be created"
              values={{ count: newKeysArray.length }}
            />
          </MessageBanner>
          <MultiColumnList
            interactive={false}
            contentData={newKeysArray}
            visibleColumns={['translationKey', 'sourceValue']}
            columnWidths={{ translationKey: '60%', sourceValue: '40%' }}
            columnMapping={{
              translationKey: intl.formatMessage({
                id: 'stripes-smart-components.actionMenu.sync.column.key',
                defaultMessage: 'Translation Key',
              }),
              sourceValue: intl.formatMessage({
                id: 'stripes-smart-components.actionMenu.sync.column.value',
                defaultMessage: 'Source Value',
              }),
            }}
            maxHeight={200}
            virtualize
          />
        </>
      ) : (
        <MessageBanner type="default">
          <FormattedMessage
            id="stripes-smart-components.actionMenu.sync.noNewKeys"
            defaultMessage="No new keys to create"
          />
        </MessageBanner>
      )}

      {hasOrphaned && (
        <>
          <br />
          <MessageBanner type="warning">
            <FormattedMessage
              id="stripes-smart-components.actionMenu.sync.orphanedCount"
              defaultMessage="{count} orphaned key(s) — exist in DB but not in current data"
              values={{ count: orphanedKeys.length }}
            />
          </MessageBanner>
          <MultiColumnList
            interactive={false}
            contentData={orphanedKeys.map((o, i) => ({
              id: i, translationKey: o.translationKey,
              locales: o.locales.join(', '),
            }))}
            visibleColumns={['translationKey', 'locales']}
            columnWidths={{ translationKey: '70%', locales: '30%' }}
            columnMapping={{
              translationKey: intl.formatMessage({
                id: 'stripes-smart-components.actionMenu.sync.column.key',
                defaultMessage: 'Translation Key',
              }),
              locales: intl.formatMessage({
                id: 'stripes-smart-components.actionMenu.sync.column.locales',
                defaultMessage: 'Locales',
              }),
            }}
            maxHeight={200}
            virtualize
          />
        </>
      )}
    </>
  );

  // ── Step 2: Result ────────────────────────────────────────────────
  const renderResult = () => (
    <>
      {result?.error ? (
        <MessageBanner type="error">
          <FormattedMessage
            id="stripes-smart-components.actionMenu.sync.error"
            defaultMessage="Operation failed: {error}"
            values={{ error: result.error }}
          />
        </MessageBanner>
      ) : (
        <>
          {result?.created > 0 && (
            <MessageBanner type="success">
              <FormattedMessage
                id="stripes-smart-components.actionMenu.sync.created"
                defaultMessage="{count} key(s) created successfully"
                values={{ count: result.created }}
              />
            </MessageBanner>
          )}
          {result?.deleted > 0 && (
            <>
              <br />
              <MessageBanner type="success">
                <FormattedMessage
                  id="stripes-smart-components.actionMenu.sync.deleted"
                  defaultMessage="{count} orphaned key(s) deleted"
                  values={{ count: result.deleted }}
                />
              </MessageBanner>
            </>
          )}
          {result?.created === 0 && result?.deleted === 0 && (
            <MessageBanner type="default">
              <FormattedMessage
                id="stripes-smart-components.actionMenu.sync.nothingDone"
                defaultMessage="No changes were made"
              />
            </MessageBanner>
          )}
        </>
      )}
    </>
  );

  // ── Footer ────────────────────────────────────────────────────────
  const renderFooter = () => {
    if (step === STEP_RESULT) {
      return (
        <ModalFooter>
          <Button buttonStyle="primary" onClick={handleClose}>
            <FormattedMessage id="stripes-core.button.close" />
          </Button>
        </ModalFooter>
      );
    }

    return (
      <ModalFooter>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {hasNew && (
            <Button
              buttonStyle="primary"
              onClick={handleCreateOnly}
              disabled={isLoading}
            >
              <Icon icon="plus-sign">
                <FormattedMessage
                  id="stripes-smart-components.actionMenu.sync.createOnly"
                  defaultMessage="Create new keys"
                />
              </Icon>
            </Button>
          )}
          {hasOrphaned && (
            <Button
              buttonStyle="danger"
              onClick={handleCreateAndDelete}
              disabled={isLoading}
            >
              <Icon icon="trash">
                <FormattedMessage
                  id={hasNew
                    ? 'stripes-smart-components.actionMenu.sync.createAndDelete'
                    : 'stripes-smart-components.actionMenu.sync.deleteOrphaned'}
                  defaultMessage={hasNew ? 'Create + Delete orphaned' : 'Delete orphaned'}
                />
              </Icon>
            </Button>
          )}
        </div>
        <Button buttonStyle="default" onClick={handleClose} disabled={isLoading}>
          <FormattedMessage id="stripes-core.button.cancel" />
        </Button>
      </ModalFooter>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="large"
      label={
        <Icon icon="plus-sign">
          <FormattedMessage
            id="stripes-smart-components.actionMenu.sync.title"
            defaultMessage="{name} — Sync Translation Keys"
            values={{ name: fileName }}
          />
        </Icon>
      }
      footer={renderFooter()}
    >
      {step === STEP_REVIEW ? renderReview() : renderResult()}
    </Modal>
  );
};

CreateNewKeysModal.propTypes = propTypes;
CreateNewKeysModal.defaultProps = defaultProps;

export default CreateNewKeysModal;
